import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../../api/client";
import { useTabParam } from "../../hooks/useTabParam";
import DropdownSelect from "../../components/DropdownSelect";
import type { Site } from "../../types";

interface Cert { id: number; site_id: number; certification_type: string; certification_body: string; status: string; issued_date: string; expiry_date: string | null; next_audit_date: string | null; }
interface Audit { id: number; audit_date: string; audit_type: string; auditor_name: string; outcome: string; observations_count: number; non_conformities_minor: number; non_conformities_major: number; non_conformities_critical: number; }
interface Plan { id: number; site_id: number; name: string; version: string; status: string; effective_date: string; review_date: string; }

const COMPLIANCE_TABS = ["certs", "haccp"] as const;

export default function AdminCompliancePage() {
  const [certs, setCerts] = useState<Cert[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [tab, setTab] = useTabParam(COMPLIANCE_TABS, "certs");
  const [showCertForm, setShowCertForm] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [selectedCert, setSelectedCert] = useState<number | null>(null);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [showAuditForm, setShowAuditForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get<Cert[]>("/api/certifications"),
        api.get<Plan[]>("/api/haccp/plans"),
        api.get<Site[]>("/api/sites"),
      ]);
      setCerts(c); setPlans(p); setSites(s);
    } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  const loadAudits = async (certId: number) => {
    try { setAudits(await api.get<Audit[]>(`/api/certifications/${certId}/audits`)); } catch {}
  };

  const selectCert = (id: number) => {
    if (selectedCert === id) { setSelectedCert(null); } else { setSelectedCert(id); void loadAudits(id); }
  };

  const siteName = (id: number) => sites.find((s) => s.id === id)?.name ?? `Site #${id}`;
  const statusTone = (s: string) => s === "active" ? "bg-green-50 text-green-700" : s === "expired" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Certifications & Audits</h1>
        {tab === "certs" && <button type="button" onClick={() => setShowCertForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Add Certification</button>}
        {tab === "haccp" && <button type="button" onClick={() => setShowPlanForm(true)} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Add HACCP Plan</button>}
      </div>
      <nav className="flex gap-1 border-b border-gray-200">
        <TabBtn active={tab === "certs"} onClick={() => setTab("certs")}>Certifications ({certs.length})</TabBtn>
        <TabBtn active={tab === "haccp"} onClick={() => setTab("haccp")}>HACCP Plans ({plans.length})</TabBtn>
      </nav>

      {showCertForm && <CertForm sites={sites} onSaved={() => { setShowCertForm(false); void load(); }} onCancel={() => setShowCertForm(false)} />}
      {showPlanForm && <PlanForm sites={sites} onSaved={() => { setShowPlanForm(false); void load(); }} onCancel={() => setShowPlanForm(false)} />}

      {tab === "certs" && certs.map((c) => (
        <div key={c.id} className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button type="button" onClick={() => selectCert(c.id)} className="flex w-full items-center justify-between px-5 py-4 text-left">
            <div>
              <p className="text-sm font-semibold text-gray-900">{c.certification_type}</p>
              <p className="text-xs text-gray-500">{c.certification_body} · {siteName(c.site_id)}</p>
              <p className="text-xs text-gray-400">Issued: {c.issued_date}{c.expiry_date ? ` · Expires: ${c.expiry_date}` : ""}</p>
            </div>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusTone(c.status)}`}>{c.status}</span>
          </button>
          {selectedCert === c.id && (
            <div className="border-t border-gray-100 px-5 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase text-gray-500">Audit History</h4>
                <button type="button" onClick={() => setShowAuditForm(true)} className="rounded border border-gray-300 px-2 py-1 text-xs">+ Add Audit</button>
              </div>
              {showAuditForm && <AuditForm certId={c.id} onSaved={() => { setShowAuditForm(false); void loadAudits(c.id); }} onCancel={() => setShowAuditForm(false)} />}
              {audits.map((a) => (
                <div key={a.id} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{a.audit_date} — {a.audit_type}</p>
                      <p className="text-xs text-gray-500">{a.auditor_name} · {a.outcome}</p>
                    </div>
                    {(a.non_conformities_minor + a.non_conformities_major + a.non_conformities_critical > 0) && (
                      <p className="text-xs text-red-600">{a.non_conformities_minor}m {a.non_conformities_major}M {a.non_conformities_critical}C</p>
                    )}
                  </div>
                </div>
              ))}
              {audits.length === 0 && <p className="text-xs text-gray-400">No audits recorded.</p>}
            </div>
          )}
        </div>
      ))}
      {tab === "certs" && certs.length === 0 && !showCertForm && <p className="text-sm text-gray-400">No certifications.</p>}

      {tab === "haccp" && plans.map((p) => (
        <div key={p.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-gray-900">{p.name} (v{p.version})</p>
          <p className="text-xs text-gray-500">{siteName(p.site_id)} · {p.status} · Effective: {p.effective_date} · Review: {p.review_date}</p>
        </div>
      ))}
      {tab === "haccp" && plans.length === 0 && !showPlanForm && <p className="text-sm text-gray-400">No HACCP plans.</p>}
    </div>
  );
}

function CertForm({ sites, onSaved, onCancel }: { sites: Site[]; onSaved: () => void; onCancel: () => void }) {
  const [siteId, setSiteId] = useState<number>(sites[0]?.id ?? 0);
  const [certType, setCertType] = useState("");
  const [certBody, setCertBody] = useState("");
  const [issued, setIssued] = useState(new Date().toISOString().slice(0, 10));
  const [expiry, setExpiry] = useState("");
  const [nextAudit, setNextAudit] = useState("");
  const [certStatus, setCertStatus] = useState("active");
  const [notes, setNotes] = useState("");
  const [certFile, setCertFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const TYPES = ["SALSA", "BRC", "SCAN", "SEDEX", "HACCP Accreditation", "ISO 22000", "FSSC 22000", "Other"];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true); setError(null);
    try {
      let docId: number | null = null;
      if (certFile) {
        const fd = new FormData();
        fd.append("file", certFile);
        const params = new URLSearchParams({ title: `${certType} Certificate`, category_id: "1", visibility: "global" });
        const docRes = await fetch(`/api/documents?${params}`, { method: "POST", body: fd, credentials: "include" });
        if (docRes.ok) { const d = await docRes.json(); docId = d.id; }
      }
      await api.post("/api/certifications", {
        site_id: siteId, certification_type: certType, certification_body: certBody,
        issued_date: issued, expiry_date: expiry || null, next_audit_date: nextAudit || null,
        status: certStatus, notes: notes || null,
        certificate_document_id: docId,
      });
      onSaved();
    } catch (err) { setError(err instanceof ApiError ? err.message : "Failed"); } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">New Certification</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Site</span>
          <select value={siteId} onChange={(e) => setSiteId(Number(e.target.value))} className="input mt-1">{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        </label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Type</span>
          <select value={certType} onChange={(e) => setCertType(e.target.value)} required className="input mt-1"><option value="">Select...</option>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Certification Body</span><input value={certBody} onChange={(e) => setCertBody(e.target.value)} required placeholder="SGS, Bureau Veritas..." className="input mt-1" /></label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Status</span>
          <DropdownSelect listCode="certification_status" value={certStatus} onChange={setCertStatus} className="input mt-1" />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Issued Date</span><input type="date" value={issued} onChange={(e) => setIssued(e.target.value)} required className="input mt-1" /></label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Expiry Date</span><input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="input mt-1" /></label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Next Audit Date</span><input type="date" value={nextAudit} onChange={(e) => setNextAudit(e.target.value)} className="input mt-1" /></label>
      </div>
      <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Certificate Document</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setCertFile(e.target.files?.[0] ?? null)} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:rounded-lg file:border-0 file:bg-pme-blue/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-pme-blue" />
      </label>
      <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Notes</span><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input mt-1" /></label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{submitting ? "Saving..." : "Save"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function AuditForm({ certId, onSaved, onCancel }: { certId: number; onSaved: () => void; onCancel: () => void }) {
  const [auditDate, setAuditDate] = useState(new Date().toISOString().slice(0, 10));
  const [auditType, setAuditType] = useState("scheduled");
  const [auditorName, setAuditorName] = useState("");
  const [auditorOrg, setAuditorOrg] = useState("");
  const [outcome, setOutcome] = useState("pending");
  const [reportFile, setReportFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true);
    try {
      let docId: number | null = null;
      if (reportFile) {
        const fd = new FormData();
        fd.append("file", reportFile);
        const params = new URLSearchParams({ title: `Audit Report ${auditDate}`, category_id: "1", visibility: "global" });
        const docRes = await fetch(`/api/documents?${params}`, { method: "POST", body: fd, credentials: "include" });
        if (docRes.ok) { const d = await docRes.json(); docId = d.id; }
      }
      await api.post(`/api/certifications/${certId}/audits`, {
        audit_date: auditDate, audit_type: auditType, auditor_name: auditorName,
        auditor_organisation: auditorOrg || null, outcome,
        report_document_id: docId,
      });
      onSaved();
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <label className="block text-xs"><span className="font-medium text-gray-700">Date</span><input type="date" value={auditDate} onChange={(e) => setAuditDate(e.target.value)} required className="input mt-1" /></label>
        <label className="block text-xs"><span className="font-medium text-gray-700">Type</span>
          <DropdownSelect listCode="audit_type" value={auditType} onChange={setAuditType} className="input mt-1" />
        </label>
        <label className="block text-xs"><span className="font-medium text-gray-700">Outcome</span>
          <DropdownSelect listCode="audit_outcome" value={outcome} onChange={setOutcome} className="input mt-1" />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block text-xs"><span className="font-medium text-gray-700">Auditor Name</span><input value={auditorName} onChange={(e) => setAuditorName(e.target.value)} required className="input mt-1" /></label>
        <label className="block text-xs"><span className="font-medium text-gray-700">Organisation</span><input value={auditorOrg} onChange={(e) => setAuditorOrg(e.target.value)} className="input mt-1" /></label>
      </div>
      <label className="block text-xs"><span className="font-medium text-gray-700">Audit Report</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setReportFile(e.target.files?.[0] ?? null)} className="mt-1 block w-full text-xs text-gray-500 file:mr-2 file:rounded file:border-0 file:bg-pme-blue/10 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-pme-blue" />
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded bg-pme-blue px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">{submitting ? "Saving..." : "Save Audit"}</button>
        <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-3 py-1 text-xs">Cancel</button>
      </div>
    </form>
  );
}

function PlanForm({ sites, onSaved, onCancel }: { sites: Site[]; onSaved: () => void; onCancel: () => void }) {
  const [siteId, setSiteId] = useState<number>(sites[0]?.id ?? 0);
  const [name, setName] = useState("");
  const [version, setVersion] = useState("1.0");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true);
    try { await api.post("/api/haccp/plans", { site_id: siteId, name, version, effective_date: effective, review_date: review || effective }); onSaved(); } catch {} finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">New HACCP Plan</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Site</span><select value={siteId} onChange={(e) => setSiteId(Number(e.target.value))} className="input mt-1">{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Plan Name</span><input value={name} onChange={(e) => setName(e.target.value)} required className="input mt-1" /></label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Version</span><input value={version} onChange={(e) => setVersion(e.target.value)} className="input mt-1" /></label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Effective Date</span><input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} required className="input mt-1" /></label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Review Date</span><input type="date" value={review} onChange={(e) => setReview(e.target.value)} className="input mt-1" /></label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{submitting ? "Saving..." : "Save"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={["px-4 py-2 text-sm font-semibold", active ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"].join(" ")}>{children}</button>;
}
